/**
 * Smart Book — the pure planner model behind the week-planner dialog.
 *
 * Everything here is side-effect free so it can be unit-tested without
 * Angular: seeding rows from the `myWeekSummary` payload, the three-resource
 * selection an office day carries, Fill down / Fill across, the Smart-fill
 * heuristic, and the derived counts the footer and the "Applies to N office
 * days" line read. The component owns signals and IO only.
 *
 * ONE SOURCE PER FACT (HANDOFF §11c). The office-day count, the booking count
 * and the Fill-across pressed state are all computed HERE from the same
 * `PlanDay[]`. No surface keeps its own tally — a second tally is how the two
 * halves of a footer end up disagreeing.
 *
 * A DAY GUESSES NOTHING. `presence: null` is the absence of a declaration, not
 * a fourth bucket, and a resource the viewer has not chosen is simply absent —
 * never a plausible default.
 */
import type { Presence } from '../../../core/constants/presence';
import { statusToPresence, isInOfficeBooking, withoutInOfficeBookings, dayInOffice } from '../../../core/constants/presence';

/**
 * The three resources an office day can take. `meetingroom` is deliberately
 * NOT here — a room is booked against a meeting, not against a day.
 */
export type ResourceKind = 'desk' | 'parking' | 'locker';

/** Display order for every resource row in the dialog. */
export const RESOURCE_KINDS: readonly ResourceKind[] = ['desk', 'parking', 'locker'] as const;

/** Per-resource booleans — used for both "the viewer wants it" and "the tenant offers it". */
export type ResourceFlags = Readonly<Record<ResourceKind, boolean>>;

/** No resource chosen. The seed state of every day: opt in, never opt out. */
export const NO_RESOURCES: ResourceFlags = Object.freeze({
  desk: false,
  parking: false,
  locker: false,
});

/**
 * The default an OFFICE day carries (HANDOFF §11b, verbatim): "defaulting to
 * desk only, so parking and a locker are opted into rather than given."
 *
 * This is the one place that decides it, and it is not a guess dressed as a
 * fact: the desk chip renders PRESSED, the footer counts it, and the primary
 * action names it. The viewer sees exactly what the commit will send and can
 * unpress it. A default you can see and undo is a default; a default you
 * cannot see is a fabrication.
 *
 * `offered` is the set of kinds the building actually stocks (see the
 * component's `offeredKinds`), so a tenant with no desks gets nothing.
 */
export function officeResourceSeed(offered: readonly ResourceKind[]): ResourceFlags {
  return { desk: offered.includes('desk'), parking: false, locker: false };
}

/**
 * Apply the office-day default across a freshly-loaded week: every editable
 * office day that does not already HOLD a desk asks for one.
 *
 * Called once per load — never on the reload that follows a failed commit,
 * where re-ticking a request that has just failed would be the surface
 * arguing with the server.
 */
export function seedOfficeDefaults(
  days: readonly PlanDay[],
  offered: readonly ResourceKind[],
): PlanDay[] {
  const seed = officeResourceSeed(offered);
  return days.map(d => {
    if (!isOfficeDay(d)) return d;
    const want: Record<ResourceKind, boolean> = { ...d.want };
    for (const kind of RESOURCE_KINDS) {
      if (seed[kind] && d.held[kind] === null) want[kind] = true;
    }
    return { ...d, want };
  });
}

/** No resource held. */
const NOTHING_HELD: Readonly<Record<ResourceKind, SmartBookBooking | null>> = Object.freeze({
  desk: null,
  parking: null,
  locker: null,
});

/** One existing booking on a day, as returned by `myWeekSummary`. */
export interface SmartBookBooking {
  _id: string;
  bookingType: string;
  spaceName: string;
  floorName?: string;
  startTime: string;
  endTime: string;
  checkedIn: boolean;
  spaceId?: string;
}

/** One day of the `myWeekSummary` payload (the parts the planner reads). */
export interface SmartBookDayDto {
  date: string;
  dayLabel: string;
  isWeekend: boolean;
  colleaguesInOffice?: number;
  bookings?: SmartBookBooking[];
  status?: { statusId: string; statusName: string };
  /** API #974; absent on an older API (then the `location` booking says it). */
  inOfficeStatus?: boolean | null;
}

/**
 * One editable day in the Smart Book week planner. `seedPresence` is the
 * booking/status-derived bucket at load time; `presence` + `want` are the
 * viewer's local plan, committed by the footer's primary action.
 */
export interface PlanDay {
  readonly date: string;
  readonly dayLabel: string;
  readonly isWeekend: boolean;
  readonly isPast: boolean;
  readonly isToday: boolean;
  readonly colleaguesInOffice: number;
  readonly bookings: readonly SmartBookBooking[];
  /**
   * The booking the viewer ALREADY holds for each resource, or null. Derived
   * from `bookings` — never stored twice (§11c). A held resource is a real
   * record: it is shown by name, and it is not a toggle.
   */
  readonly held: Readonly<Record<ResourceKind, SmartBookBooking | null>>;
  /**
   * NULL means the viewer has declared nothing for this day. It is the absence
   * of a bucket, never a bucket of its own — see {@link toPlanDay}.
   */
  readonly seedPresence: Presence | null;
  readonly presence: Presence | null;
  /** NEW requests this plan will book. Never true for a kind already held. */
  readonly want: ResourceFlags;
}

/** A planner row the viewer has actually declared a bucket for. */
export type DeclaredPlanDay = PlanDay & { readonly presence: Presence };

/** One resource the plan will book — the unit the commit step zips over. */
export interface PlannedBooking {
  readonly date: string;
  readonly kind: ResourceKind;
}

/**
 * Seed an editable planner row from one `myWeekSummary` day. Presence is
 * derived: any workspace booking ⇒ Office; else the stored status's bucket;
 * else NULL — the viewer has declared nothing. `want` always starts empty:
 * those are NEW requests, and existing bookings render as named held chips.
 *
 * WHY "no declaration" IS NOT `elsewhere` (this was the bug)
 *   Seeding an undeclared day as `elsewhere` (a) pressed the "Elsewhere"
 *   segment for a viewer who had chosen nothing — the planner asserting a
 *   declaration nobody made — and (b) made a genuine "Elsewhere" choice
 *   invisible to {@link presenceDeltas}, so the commit wrote nothing and the
 *   segment silently reverted on the next open. The sibling mapper in
 *   `features/home-preview/components/your-week/presence.util.ts` must keep
 *   agreeing that null is the absence of a bucket.
 */
export function toPlanDay(dto: SmartBookDayDto, today: string): PlanDay {
  // The in-office status (a `location` booking) is Office, never a held space.
  const bookings = withoutInOfficeBookings(dto.bookings ?? []);
  const seedPresence: Presence | null = bookings.length > 0 || dayInOffice(dto)
    ? 'office'
    : statusToPresence(dto.status?.statusId, dto.status?.statusName);
  return {
    date: dto.date,
    dayLabel: dto.dayLabel,
    isWeekend: dto.isWeekend,
    isPast: dto.date < today,
    isToday: dto.date === today,
    colleaguesInOffice: dto.colleaguesInOffice ?? 0,
    bookings,
    held: heldByKind(bookings),
    seedPresence,
    presence: seedPresence,
    want: NO_RESOURCES,
  };
}

/**
 * Fill the API's one unavoidable presence gap with a confirmed local write.
 *
 * Explicit server state (a booking or stored Home/Elsewhere/Out row) always
 * wins. Only a null seed can use the fallback, which is principally how a
 * successful `clearStatus` remains recognisable as Office after a reload.
 */
export function withConfirmedPresence(
  day: PlanDay,
  confirmed: Presence | null,
): PlanDay {
  if (day.seedPresence !== null || confirmed === null) return day;
  return {
    ...day,
    seedPresence: confirmed,
    presence: confirmed,
  };
}

/** Index a day's bookings by resource kind. Rooms and anything else are ignored. */
function heldByKind(
  bookings: readonly SmartBookBooking[],
): Readonly<Record<ResourceKind, SmartBookBooking | null>> {
  if (bookings.length === 0) return NOTHING_HELD;
  const held: Record<ResourceKind, SmartBookBooking | null> = {
    desk: null,
    parking: null,
    locker: null,
  };
  for (const booking of bookings) {
    const kind = booking.bookingType as ResourceKind;
    if (kind in held && held[kind] === null) held[kind] = booking;
  }
  return held;
}

/** A day the viewer can still plan — a weekday that isn't in the past. */
export function isEditable(day: PlanDay): boolean {
  return !day.isWeekend && !day.isPast;
}

/** True when the day holds at least one desk / parking / locker booking. */
export function holdsAnything(day: PlanDay): boolean {
  return RESOURCE_KINDS.some(kind => day.held[kind] !== null);
}

/**
 * The one row the planner tints — the design's "selected day".
 *
 * A preferred date (the caller's preset) wins; otherwise today, when today is
 * a weekday in the loaded week; otherwise the first day still plannable. Null
 * when nothing in the week can be planned, so the tint is simply absent
 * rather than parked on an arbitrary row.
 */
export function focusedDate(
  days: readonly PlanDay[],
  preferred?: string,
): string | null {
  if (preferred && days.some(d => d.date === preferred && isEditable(d))) return preferred;
  const today = days.find(d => d.isToday && isEditable(d));
  if (today) return today.date;
  return days.find(isEditable)?.date ?? null;
}

/** A day the plan says the viewer is in the office. The ONE office predicate. */
export function isOfficeDay(day: PlanDay): boolean {
  return isEditable(day) && day.presence === 'office';
}

/** Every office day in the plan — the set every "applies to" count reads. */
export function officeDays(days: readonly PlanDay[]): PlanDay[] {
  return days.filter(isOfficeDay);
}

/**
 * The resources this plan will actually book: every office day's wanted kinds,
 * minus anything already held. The footer's booking count and the commit
 * batch read the SAME list, so the button can never promise a different
 * number from the one it sends.
 */
export function plannedBookings(days: readonly PlanDay[]): PlannedBooking[] {
  const planned: PlannedBooking[] = [];
  for (const day of days) {
    if (!isOfficeDay(day)) continue;
    for (const kind of RESOURCE_KINDS) {
      if (day.want[kind] && day.held[kind] === null) planned.push({ date: day.date, kind });
    }
  }
  return planned;
}

/**
 * Editable days whose presence differs from the loaded state — i.e. the days
 * the commit must write.
 *
 * A day still sitting at NULL is excluded by construction: null is the absence
 * of a declaration, and `PresenceWriterService` has no write path for "not
 * set" (Office is `clearStatus`, the other three are `setStatus`). Returning
 * {@link DeclaredPlanDay} makes that guarantee visible to callers, so
 * `persist(date, presence)` can never be handed a null.
 */
export function presenceDeltas(days: readonly PlanDay[]): DeclaredPlanDay[] {
  return days.filter(
    (d): d is DeclaredPlanDay =>
      isEditable(d) && d.presence !== null && d.presence !== d.seedPresence,
  );
}

/**
 * True when this presence option is not selectable for the day — the platform
 * rejects a manual status while an in-office booking exists, so the non-Office
 * segments lock until the booking is released. The control renders the lock
 * (disabled + aria-disabled + a linked reason); it never accepts the click and
 * silently discards it.
 */
export function presenceIsLocked(day: PlanDay, option: Presence): boolean {
  // The in-office status (a `location` booking) does not lock: the writer
  // clears it before setting another status.
  return option !== 'office' && day.bookings.some((b) => !isInOfficeBooking(b.bookingType));
}

/**
 * Set the presence for one day. Weekends, past days and locked options are
 * no-ops. Leaving Office withdraws that day's resource requests — a resource
 * you asked for on a day you are no longer in is not a choice, it is a leak.
 *
 * Arriving AT Office applies the §11b default (a desk, when the building
 * stocks one and the day does not already hold one) so a day set to Office by
 * hand carries the same default as a day that loaded as Office. `offered`
 * defaults to empty, which is the "no default at all" behaviour.
 */
export function withPresence(
  days: readonly PlanDay[],
  date: string,
  presence: Presence,
  offered: readonly ResourceKind[] = [],
): PlanDay[] {
  return days.map(d => {
    if (d.date !== date || !isEditable(d)) return d;
    if (presenceIsLocked(d, presence)) return d;
    if (presence !== 'office') return { ...d, presence, want: NO_RESOURCES };
    const seed = officeResourceSeed(offered);
    const want: Record<ResourceKind, boolean> = { ...d.want };
    for (const kind of RESOURCE_KINDS) {
      if (seed[kind] && d.held[kind] === null) want[kind] = true;
    }
    return { ...d, presence, want };
  });
}

/**
 * Toggle one resource on one office day. A kind the viewer already holds is a
 * real booking, not a toggle — it is left alone.
 */
export function toggleResource(
  days: readonly PlanDay[],
  date: string,
  kind: ResourceKind,
): PlanDay[] {
  return days.map(d => {
    if (d.date !== date || !isOfficeDay(d) || d.held[kind] !== null) return d;
    return { ...d, want: { ...d.want, [kind]: !d.want[kind] } };
  });
}

/**
 * Fill across — apply ONE resource to every office day at once (HANDOFF §11b).
 * Days that already hold that kind are untouched: they already have it.
 */
export function fillAcross(
  days: readonly PlanDay[],
  kind: ResourceKind,
  on: boolean,
): PlanDay[] {
  return days.map(d => {
    if (!isOfficeDay(d) || d.held[kind] !== null) return d;
    return { ...d, want: { ...d.want, [kind]: on } };
  });
}

/**
 * Fill across reads as PRESSED only when every office day has that resource —
 * held or requested. With no office day it is not pressed and the control is
 * disabled, so appearance, accessible state and behaviour agree.
 */
export function fillAcrossPressed(days: readonly PlanDay[], kind: ResourceKind): boolean {
  const office = officeDays(days);
  return office.length > 0 && office.every(d => d.want[kind] || d.held[kind] !== null);
}

/**
 * Fill down — copy one day's whole setup to the rest of the week: presence
 * always, resources only when the source is an office day (HANDOFF §11b).
 * A later day that holds a booking keeps it, and cannot be pushed off Office
 * by the copy (that is the locked case).
 */
export function fillDown(days: readonly PlanDay[], fromDate: string): PlanDay[] {
  const sourceIndex = days.findIndex(d => d.date === fromDate);
  if (sourceIndex < 0) return [...days];
  const source = days[sourceIndex];
  if (!isEditable(source) || source.presence === null) return [...days];
  const presence = source.presence;
  const office = presence === 'office';

  return days.map((d, index) => {
    if (index <= sourceIndex || !isEditable(d)) return d;
    if (presenceIsLocked(d, presence)) return d;
    if (!office) return { ...d, presence, want: NO_RESOURCES };
    const want: Record<ResourceKind, boolean> = { ...NO_RESOURCES };
    for (const kind of RESOURCE_KINDS) {
      const inSource = source.want[kind] || source.held[kind] !== null;
      want[kind] = inSource && d.held[kind] === null;
    }
    return { ...d, presence, want };
  });
}

/** True when there is at least one later editable day to copy into. */
export function canFillDown(days: readonly PlanDay[], fromDate: string): boolean {
  const sourceIndex = days.findIndex(d => d.date === fromDate);
  if (sourceIndex < 0) return false;
  return days.slice(sourceIndex + 1).some(isEditable);
}

/** Outcome of a Smart-fill pass. */
export interface SmartFillOutcome {
  readonly days: PlanDay[];
  /** Rows whose presence or desk request actually changed. */
  readonly toggled: number;
  /** True when the favourite-colleague signal narrowed the fill. */
  readonly usedColleagueSignal: boolean;
}

/**
 * Smart-fill — pre-select Office + Desk using the colleague signal: the
 * weekdays where the viewer's favourite people are in win; when no favourite
 * has booked, every remaining weekday is filled. Deliberate Home / Out choices
 * are always respected, and only a DESK is requested — parking and a locker
 * are opted into, never given (HANDOFF §11b).
 */
export function applySmartFill(
  days: readonly PlanDay[],
  canDesk: boolean,
): SmartFillOutcome {
  const colleagueDates = new Set(
    days.filter(d => isEditable(d) && d.colleaguesInOffice > 0).map(d => d.date),
  );
  const usedColleagueSignal = colleagueDates.size > 0;
  let toggled = 0;

  const next = days.map(d => {
    if (!isEditable(d)) return d;
    if (d.presence === 'home' || d.presence === 'off') return d;
    if (usedColleagueSignal && !colleagueDates.has(d.date)) return d;
    const desk = canDesk && d.held.desk === null ? true : d.want.desk;
    if (d.presence !== 'office' || desk !== d.want.desk) toggled++;
    return { ...d, presence: 'office' as const, want: { ...d.want, desk } };
  });

  return { days: next, toggled, usedColleagueSignal };
}

/**
 * The days the viewer's favourite people are mostly in, e.g. "Tue & Wed" —
 * the top two colleague days in date order. Empty string when no favourite
 * has booked this week, so the caller renders nothing rather than a guess.
 */
export function topColleagueDayLabels(days: readonly PlanDay[]): string {
  return days
    .filter(d => isEditable(d) && d.colleaguesInOffice > 0)
    .sort((a, b) => b.colleaguesInOffice - a.colleaguesInOffice)
    .slice(0, 2)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(d => d.dayLabel)
    .join(' & ');
}

/**
 * Apply a caller preset — pre-select (date, kind) as Office + that resource.
 * With no explicit date the first plannable day wins (widget "Book parking").
 * Days that already hold that resource kind are left untouched.
 */
export function applyPreset(
  days: readonly PlanDay[],
  date: string | undefined,
  kind: ResourceKind,
): PlanDay[] {
  const targetDate = date ?? days.find(isEditable)?.date;
  if (!targetDate) return [...days];

  return days.map(d => {
    if (d.date !== targetDate || !isEditable(d)) return d;
    return {
      ...d,
      presence: 'office' as const,
      want: { ...d.want, [kind]: d.held[kind] === null ? true : d.want[kind] },
    };
  });
}

/**
 * "Mon 21 Jul – Fri 25 Jul" — the working span of the loaded week, derived
 * from the first and last WEEKDAY rows (the weekend rows are inert, so they
 * are not part of the span the header names). Empty when nothing is loaded.
 *
 * Dates are anchored at local noon so a UTC-behind timezone cannot roll the
 * label back a day (CLAUDE.md § Timezone).
 */
export function weekRangeLabel(days: readonly PlanDay[], locale: string): string {
  const weekdays = days.filter(d => !d.isWeekend);
  const span = weekdays.length > 0 ? weekdays : days;
  if (span.length === 0) return '';
  const first = formatSpanDate(span[0].date, locale);
  const last = formatSpanDate(span[span.length - 1].date, locale);
  return first === last ? first : `${first} – ${last}`;
}

function formatSpanDate(date: string, locale: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}
